import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layers, Loader2, Save, X } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import {
  DEFAULT_EXPENSE_LEGEND_ROWS,
  ExpenseLegendRow,
  QuickBooksAccountRow,
  fetchExpenseLegend,
  fetchQuickBooksChartOfAccountsMeta,
  legendAccountGroupLabels,
  legendQbAccountOptions,
  qbAccountShortLabel,
  saveExpenseLegend,
  syncQuickBooksAccountsFromCloud,
} from '../../utils/expenseLegend';
import '../pm/pm-shared.css';
import './ExpenseGroupManagerModal.css';

interface Props {
  onClose: () => void;
  onSaved?: (rows: ExpenseLegendRow[]) => void;
}

function rowsSnapshot(rows: ExpenseLegendRow[]): string {
  return JSON.stringify(rows);
}

const ExpenseGroupManagerModal: React.FC<Props> = ({ onClose, onSaved }) => {
  const { user } = useAuth();
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const [rows, setRows] = useState<ExpenseLegendRow[]>(DEFAULT_EXPENSE_LEGEND_ROWS);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [accounts, setAccounts] = useState<QuickBooksAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coaSyncedAt, setCoaSyncedAt] = useState<Date | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [groupSearch, setGroupSearch] = useState('');
  const [accountSearch, setAccountSearch] = useState('');

  const dirty = rowsSnapshot(rows) !== savedSnapshot;

  const expenseAccounts = useMemo(
    () =>
      accounts.filter(
        a =>
          a.active &&
          (a.classification === 'Expense' ||
            a.accountType === 'Expense' ||
            a.accountType === 'Cost of Goods Sold' ||
            a.fullyQualifiedName.toLowerCase().includes('cogs')),
      ),
    [accounts],
  );

  const accountGroupLabels = useMemo(() => legendAccountGroupLabels(rows), [rows]);

  const projectGroups = useMemo(
    () => rows.filter(r => r.plBucket !== 'overhead'),
    [rows],
  );

  const overheadGroups = useMemo(
    () => rows.filter(r => r.plBucket === 'overhead'),
    [rows],
  );

  const filterGroups = useCallback(
    (list: ExpenseLegendRow[]) => {
      const q = groupSearch.trim().toLowerCase();
      if (!q) return list;
      return list.filter(
        r =>
          r.label.toLowerCase().includes(q) ||
          r.plBucket.toLowerCase().includes(q) ||
          r.id.toLowerCase().includes(q),
      );
    },
    [groupSearch],
  );

  const selectedRow = useMemo(
    () => (selectedId ? rows.find(r => r.id === selectedId) || null : null),
    [rows, selectedId],
  );

  const selectedAccounts = useMemo(
    () => legendQbAccountOptions(selectedRow),
    [selectedRow],
  );

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('You have unsaved changes. Close without saving?')) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [legend, coa] = await Promise.all([
          fetchExpenseLegend(),
          fetchQuickBooksChartOfAccountsMeta(),
        ]);
        if (!cancelled) {
          setRows(legend);
          setSavedSnapshot(rowsSnapshot(legend));
          setAccounts(coa.accounts);
          setCoaSyncedAt(coa.syncedAt);
          setSelectedId(legend[0]?.id || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [requestClose]);

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  const markDirty = useCallback(() => {
    setMessage(null);
    setError(null);
  }, []);

  const updateRow = useCallback(
    (id: string, patch: Partial<ExpenseLegendRow>) => {
      markDirty();
      setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
    },
    [markDirty],
  );

  const addOverheadRow = useCallback(() => {
    markDirty();
    const newId = `overhead-${Date.now().toString(36)}`;
    setRows(prev => {
      const maxSort = prev.reduce((m, r) => Math.max(m, r.sortOrder || 0), 0);
      return [
        ...prev,
        {
          id: newId,
          label: '',
          plBucket: 'overhead' as const,
          sortOrder: maxSort + 10,
          active: true,
        },
      ];
    });
    setSelectedId(newId);
  }, [markDirty]);

  const removeRow = useCallback(
    (id: string) => {
      markDirty();
      setRows(prev => prev.filter(r => r.id !== id));
      setSelectedId(prev => (prev === id ? null : prev));
    },
    [markDirty],
  );

  const addAccountToGroup = useCallback(
    (rowId: string, qbAccountId: string) => {
      const acct = accounts.find(a => a.qbId === qbAccountId);
      if (!acct) return;
      markDirty();
      setRows(prev =>
        prev.map(r => {
          if (r.id !== rowId) return r;
          const current = legendQbAccountOptions(r);
          if (current.some(o => o.qbAccountId === qbAccountId)) return r;
          return {
            ...r,
            qbAccounts: [
              ...current,
              { qbAccountId, qbAccountName: acct.fullyQualifiedName || acct.name },
            ],
            qbAccountId: undefined,
            qbAccountName: undefined,
          };
        }),
      );
      setAccountSearch('');
    },
    [accounts, markDirty],
  );

  const removeAccountFromGroup = useCallback(
    (rowId: string, qbAccountId: string) => {
      markDirty();
      setRows(prev =>
        prev.map(r => {
          if (r.id !== rowId) return r;
          const remaining = legendQbAccountOptions(r).filter(o => o.qbAccountId !== qbAccountId);
          return { ...r, qbAccounts: remaining, qbAccountId: undefined, qbAccountName: undefined };
        }),
      );
    },
    [markDirty],
  );

  const isRemovable = useCallback(
    (row: ExpenseLegendRow) =>
      row.plBucket === 'overhead' && !DEFAULT_EXPENSE_LEGEND_ROWS.some(d => d.id === row.id),
    [],
  );

  const selectedAccountIds = useMemo(
    () => new Set(selectedAccounts.map(a => a.qbAccountId)),
    [selectedAccounts],
  );

  const filteredCoaAccounts = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    const sorted = [...expenseAccounts].sort((a, b) =>
      a.fullyQualifiedName.localeCompare(b.fullyQualifiedName),
    );
    if (!q) return sorted;
    return sorted.filter(a => a.fullyQualifiedName.toLowerCase().includes(q));
  }, [expenseAccounts, accountSearch]);

  const toggleAccountInGroup = useCallback(
    (rowId: string, qbAccountId: string, checked: boolean) => {
      if (checked) addAccountToGroup(rowId, qbAccountId);
      else removeAccountFromGroup(rowId, qbAccountId);
    },
    [addAccountToGroup, removeAccountFromGroup],
  );

  const syncCoa = async () => {
    setSyncing(true);
    setError(null);
    setMessage(null);
    try {
      const result = await syncQuickBooksAccountsFromCloud();
      setAccounts(result.accounts);
      setCoaSyncedAt(new Date());
      setMessage(`Synced ${result.count} accounts from QuickBooks.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await saveExpenseLegend(rows, user?.id);
      setSavedSnapshot(rowsSnapshot(rows));
      setMessage('Card expense groups saved.');
      onSaved?.(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const renderGroupButton = (row: ExpenseLegendRow) => {
    const missing = legendQbAccountOptions(row).length === 0;
    return (
      <button
        key={row.id}
        type="button"
        className={`egm-group-btn${selectedId === row.id ? ' egm-group-btn--active' : ''}`}
        onClick={() => {
          setSelectedId(row.id);
          setAccountSearch('');
        }}
      >
        <span className="egm-group-btn-label">{row.label || '(untitled)'}</span>
        {missing && <span className="egm-missing-dot" title="No QuickBooks accounts mapped" />}
      </button>
    );
  };

  const previewLabels =
    selectedAccounts.length > 0
      ? selectedAccounts.map(a => qbAccountShortLabel(a)).join(' · ')
      : '(no accounts — QB write-back will skip)';

  return (
    <div className="egm-overlay" onClick={requestClose} role="presentation">
      <div
        className="egm-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="egm-title"
      >
        <header className="egm-header">
          <div className="egm-header-text">
            <div className="egm-header-icon" aria-hidden>
              <Layers size={18} />
            </div>
            <div>
              <h2 id="egm-title">Card expense groups</h2>
              <p>
                Define categories cardholders pick on My Card Expenses. Each group maps to one or more
                QuickBooks accounts for automatic write-back.
              </p>
              {accounts.length > 0 && (
                <div className="egm-header-meta">
                  {accounts.length} QB accounts cached
                  {coaSyncedAt
                    ? ` · last synced ${coaSyncedAt.toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}`
                    : ''}
                  {' · auto-syncs daily at 5:30 AM'}
                </div>
              )}
            </div>
          </div>
          <div className="egm-header-actions">
            <button
              type="button"
              className="egm-btn egm-btn--ghost"
              disabled={syncing || loading}
              onClick={syncCoa}
            >
              {syncing ? 'Syncing…' : 'Pull chart of accounts'}
            </button>
            <button
              ref={closeBtnRef}
              type="button"
              className="egm-close"
              onClick={requestClose}
              aria-label="Close expense group manager"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        {loading ? (
          <div className="egm-loading">
            <Loader2 size={24} className="egm-spin" aria-hidden />
            Loading groups…
          </div>
        ) : (
          <>
            <div className="egm-body">
              <aside className="egm-sidebar">
                <div className="egm-sidebar-search">
                  <input
                    type="search"
                    placeholder="Search groups…"
                    value={groupSearch}
                    onChange={e => setGroupSearch(e.target.value)}
                    aria-label="Search expense groups"
                  />
                </div>
                <div className="egm-sidebar-list">
                  {filterGroups(projectGroups).length > 0 && (
                    <>
                      <div className="egm-sidebar-section-label">Project categories</div>
                      {filterGroups(projectGroups).map(renderGroupButton)}
                    </>
                  )}
                  {filterGroups(overheadGroups).length > 0 && (
                    <>
                      <div className="egm-sidebar-section-label">Overhead categories</div>
                      {filterGroups(overheadGroups).map(renderGroupButton)}
                    </>
                  )}
                  {filterGroups(projectGroups).length === 0 &&
                    filterGroups(overheadGroups).length === 0 && (
                      <p style={{ padding: '8px', fontSize: 12, color: '#94a3b8' }}>No groups match.</p>
                    )}
                </div>
                <div className="egm-sidebar-footer">
                  <button type="button" className="egm-add-overhead" onClick={addOverheadRow}>
                    + Add overhead group
                  </button>
                </div>
              </aside>

              <div className="egm-editor">
                <div className="egm-editor-scroll">
                  {!selectedRow ? (
                    <div className="egm-editor-empty">Select a group from the sidebar.</div>
                  ) : (
                    <>
                      <div className="egm-field">
                        <label htmlFor="egm-card-label">Card label</label>
                        <input
                          id="egm-card-label"
                          type="text"
                          value={selectedRow.label}
                          onChange={e => updateRow(selectedRow.id, { label: e.target.value })}
                          placeholder="e.g. Materials"
                        />
                      </div>
                      <div className="egm-field">
                        <label>P&amp;L bucket</label>
                        <span className="egm-pl-badge">{selectedRow.plBucket}</span>
                      </div>
                      <div className="egm-field egm-field--coa">
                        <div className="egm-coa-header">
                          <label htmlFor="egm-coa-search">QuickBooks accounts in this group</label>
                          <span className="egm-coa-count">
                            {selectedAccounts.length} selected
                            {selectedAccounts.length > 1 ? ' · first checked = default' : ''}
                          </span>
                        </div>
                        {selectedAccounts.length === 0 && (
                          <p className="egm-no-accounts">
                            No accounts selected — saves in this category won&apos;t sync to QuickBooks.
                          </p>
                        )}
                        {expenseAccounts.length === 0 ? (
                          <p className="egm-coa-empty">
                            No expense accounts cached. Use &quot;Pull chart of accounts&quot; above.
                          </p>
                        ) : (
                          <>
                            <input
                              id="egm-coa-search"
                              type="search"
                              className="egm-coa-search"
                              placeholder="Filter accounts…"
                              value={accountSearch}
                              onChange={e => setAccountSearch(e.target.value)}
                              aria-label="Filter chart of accounts"
                            />
                            <div className="egm-coa-list" role="group" aria-label="Chart of accounts">
                              {filteredCoaAccounts.length === 0 ? (
                                <p className="egm-coa-empty">No accounts match your filter.</p>
                              ) : (
                                filteredCoaAccounts.map(a => {
                                  const checked = selectedAccountIds.has(a.qbId);
                                  const otherGroup = accountGroupLabels.get(a.qbId);
                                  const isDefault =
                                    checked &&
                                    selectedAccounts.length > 1 &&
                                    selectedAccounts[0]?.qbAccountId === a.qbId;
                                  return (
                                    <label
                                      key={a.qbId}
                                      className={`egm-coa-row${checked ? ' egm-coa-row--checked' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={e =>
                                          toggleAccountInGroup(
                                            selectedRow.id,
                                            a.qbId,
                                            e.target.checked,
                                          )
                                        }
                                      />
                                      <span className="egm-coa-row-text">
                                        <span className="egm-coa-row-name">{a.fullyQualifiedName}</span>
                                        {(otherGroup && otherGroup !== selectedRow.label) || isDefault ? (
                                          <span className="egm-coa-row-meta">
                                            {isDefault && <span className="egm-coa-default">Default</span>}
                                            {otherGroup && otherGroup !== selectedRow.label && (
                                              <span>In {otherGroup}</span>
                                            )}
                                          </span>
                                        ) : null}
                                      </span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      {isRemovable(selectedRow) && (
                        <div className="egm-remove-group">
                          <button type="button" onClick={() => removeRow(selectedRow.id)}>
                            Remove this overhead group
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {selectedRow && (
                  <div className="egm-preview">
                    Cardholder sees: <strong>{selectedRow.label || '(untitled)'}</strong>
                    <span className="egm-preview-arrow">→</span>
                    <strong>{previewLabels}</strong>
                  </div>
                )}
              </div>
            </div>

            <footer className="egm-footer">
              {error && <span className="egm-footer-msg egm-footer-msg--err">{error}</span>}
              {!error && message && (
                <span className="egm-footer-msg egm-footer-msg--ok">{message}</span>
              )}
              {!error && !message && dirty && (
                <span className="egm-footer-msg">Unsaved changes</span>
              )}
              <div className="egm-footer-actions">
                <button type="button" className="egm-btn egm-btn--ghost" onClick={requestClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="egm-btn egm-btn--primary"
                  disabled={saving || !dirty}
                  onClick={save}
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="egm-spin" aria-hidden />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save size={14} aria-hidden />
                      Save groups
                    </>
                  )}
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default ExpenseGroupManagerModal;
